import React, { useMemo } from 'react';
import { Autocomplete, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import { IdName, PharmacyOption } from '../types';

/** Краткое описание выбора: до двух названий, дальше - «+N» */
export const summary = (names: string[]): string => (
  names.length <= 2 ? names.join(', ') : `${names[0]}, ${names[1]} +${names.length - 2}`
);

interface PlaceProps {
  label: string;
  emptyHint: string;
  noOptions: string;
  options: PharmacyOption[];
  /** Выбранные GUID */
  value: string[];
  onChange: (guids: string[]) => void;
  minWidth?: number;
  maxWidth?: number;
  fullWidth?: boolean;
}

/** Мультивыбор аптек / складов: значения хранятся GUID'ами */
export const PlaceFilter: React.FC<PlaceProps> = ({ label, emptyHint, noOptions, options, value, onChange, minWidth = 220, maxWidth, fullWidth }) => {
  const byGuid = useMemo(() => {
    const map: Record<string, PharmacyOption> = {};
    options.forEach(o => { map[o.guid] = o; });
    return map;
  }, [options]);
  const selected = useMemo(
    () => value.map(g => byGuid[g]).filter((o): o is PharmacyOption => Boolean(o)),
    [value, byGuid]
  );

  return (
    <Autocomplete<PharmacyOption, true>
      multiple size="small" limitTags={1} disableCloseOnSelect fullWidth={fullWidth}
      sx={{ minWidth, maxWidth }}
      options={options} value={selected}
      onChange={(_, v) => onChange(v.map(o => o.guid))}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.guid === b.guid}
      renderInput={params => <TextField {...params} label={label} placeholder={selected.length ? '' : emptyHint} />}
      noOptionsText={noOptions} clearText="Сбросить"
    />
  );
};

interface IdProps {
  label: string;
  allText: string;
  options: IdName[];
  value: number[];
  onChange: (ids: number[]) => void;
  minWidth?: number;
  fullWidth?: boolean;
}

/** Мультивыбор из справочника по числовым id (типы и статусы накладных) */
export const IdMultiSelect: React.FC<IdProps> = ({ label, allText, options, value, onChange, minWidth = 200, fullWidth }) => {
  const names = useMemo(() => {
    const map: Record<number, string> = {};
    options.forEach(o => { map[o.id] = o.name; });
    return map;
  }, [options]);

  return (
    <FormControl size="small" fullWidth={fullWidth} sx={{ minWidth }}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        multiple displayEmpty notched label={label} value={value}
        onChange={e => onChange((e.target.value as Array<number | string>).map(Number))}
        renderValue={sel => (sel.length ? summary(sel.map(id => names[id] || String(id))) : allText)}
      >
        {options.map(o => (
          <MenuItem key={o.id} value={o.id}>
            <Checkbox size="small" checked={value.indexOf(o.id) !== -1} />
            <ListItemText primary={o.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
